import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { debounce, Chip } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import Loader from "../../../../components/Loader/Loader";
import "../../../../components/common/css/datatable.scss";
import {
  FUNDING_STAGE_COLORS,
  formatFundingStageLabel,
  normalizeFundingStageKey,
} from "../../../../utils/dealFlowColumns";
import { getErrorMessage } from "../../../../utils/errorUtils";

const InProgressCamp = () => {
  const [loading, setLoading] = useState(false);
  const [pageState, setPageState] = useState({
    data: [],
    total: 0,
    page: 1,
    pageSize: 9,
  });
  const [searchTerm, setSearchTerm] = useState("");

  const fetchuserCampaignData = useCallback(() => {
    const token = localStorage.getItem("accessToken");
    setLoading(true);
    setPageState((old) => ({ ...old }));
    var config = {
      method: "post",
      url: process.env.REACT_APP_API + "get-campaign-list-admin",
      headers: {
        "x-access-token": token,
      },
      data: {
        status: 1,
        campaign_status: 1,
        is_deleted: 0,
        page: pageState.page,
        limit: pageState.pageSize,
        searchTerm: searchTerm,
        isForAdminReview: 1
      },
    };

    axios(config)
      .then(function (response) {
        setPageState((old) => ({
          ...old,
          data: response.data.data.rows,
          total: response.data.data.count,
        }));
        setLoading(false);
      })
      .catch(function (error) {
        setLoading(false);
        const errorMessage = getErrorMessage(
          error,
          "Failed to load initial approval campaigns"
        );
        Swal.fire({
          title: errorMessage,
          timerProgressBar: true,
          icon: "error",
          confirmButtonText: "OK",
          showCloseButton: true,
        });
      });
  }, [pageState.page, pageState.pageSize, searchTerm]);

  const handleQuery = useMemo(
    () =>
      debounce((e) => {
        setSearchTerm(e.target.value);
        setPageState((old) => ({ ...old, page: 1 }));
      }, 1000),
    []
  );

  useEffect(() => {
    fetchuserCampaignData();
  }, [fetchuserCampaignData]);

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      width: 200,
      renderCell: (params) => {
        return (
          <div className="cellAction">
            <Link
              to={`/initial-approve/${params.row.id} `}
              style={{ textDecoration: "none" }}
            >
              <div className="viewButton">
                <RemoveRedEyeOutlinedIcon />
              </div>
            </Link>
          </div>
        );
      },
    },
  ];

  const userColumns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "corporation_legal_name",
      headerName: "Campaign Name",
      width: 170,
    },
    // Deal Flow: Funding Stage Column
    {
      field: "funding_stage",
      headerName: "Stage",
      width: 120,
      renderCell: (params) => {
        const stageKey = normalizeFundingStageKey(params.row.funding_stage);
        if (!stageKey) {
          return <span style={{ color: "#999" }}>—</span>;
        }
        const colors = FUNDING_STAGE_COLORS[stageKey] || {
          bg: "#f5f5f5",
          color: "#666",
        };
        return (
          <Chip
            label={formatFundingStageLabel(stageKey)}
            size="small"
            sx={{
              backgroundColor: colors.bg,
              color: colors.color,
              fontWeight: 500,
              fontSize: "11px",
            }}
          />
        );
      },
    },
    {
      field: "city",
      headerName: "Location",
      width: 170,
    },
    {
      field: "craax_account",
      headerName: "CRA Tax Account",
      width: 170,
    },
    {
      field: "entitytype",
      headerName: "Entity Type",
      width: 170,
    },
    {
      field: "website",
      headerName: "Website",
      width: 170,
    },
    {
      field: "camp_email",
      headerName: "Email",
      width: 200,
    },
    {
      field: "type",
      headerName: "Type",
      width: 200,
    },
  ];

  return (
    <div className="datatable">
      <Loader isLoading={loading} />
      <div className="datatableTitle">
        <div className="tablesearch">
          <input type="text" placeholder="Search..." onChange={handleQuery} />
          <SearchOutlinedIcon className="sarchicon" />
        </div>
        <h5 style={{ marginLeft: "-10%" }}>Campaigns - Initial Approval</h5>
        <div></div>
      </div>
      <DataGrid
        className="datagrid"
        autoHeight
        rows={pageState.data.filter((data) => {
          return Object.keys(data).some((key) =>
            data[key]
              ?.toString()
              .toLowerCase()
              .includes(searchTerm.toString().toLowerCase())
          );
        })}
        rowCount={pageState.total}
        rowsPerPageOptions={[10, 30, 50, 70, 100]}
        pagination
        page={pageState.page - 1}
        pageSize={pageState.pageSize}
        paginationMode="server"
        onPageChange={(newPage) => {
          setPageState((old) => ({ ...old, page: newPage + 1 }));
        }}
        onPageSizeChange={(newPageSize) =>
          setPageState((old) => ({ ...old, pageSize: newPageSize }))
        }
        columns={userColumns.concat(actionColumn)}
      />
    </div>
  );
};

export default InProgressCamp;
